import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Badge, Spinner, Alert,
  ProgressBar, Tabs, Tab
} from 'react-bootstrap'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign, faChartLine, faCalendar,
  faUsers, faCoins, faHistory, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { backendUrl } from '../utils/variables';

const RevenueDashboard = () => {
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${backendUrl}/fetch_platform_revenue.php`);
      const data = await response.json();

      if (data.success) {
        setRevenueData(data);
      } else {
        setError(data.error || 'Failed to fetch revenue data');
      }
    } catch (err) {
      setError('Failed to fetch revenue data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return '0 Coins';
    }
    return `${amount.toLocaleString()} Coins`;
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    
    // Safari-compatible date parsing
    const isoString = dateString.replace(' ', 'T');
    
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading revenue data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
      </Alert>
    );
  }

  if (!revenueData) {
    return (
      <Alert variant="info">
        No revenue data available.
      </Alert>
    );
  }

  return (
    <div className="revenue-dashboard">
      <h2 className="mb-4">
        <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
        Platform Revenue Dashboard
      </h2>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="overview" title="Overview">
          <Row>
            {/* Total Revenue Card */}
            <Col md={6} lg={3} className="mb-4">
              <Card className="h-100 border-success">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faDollarSign} size="2x" className="text-success mb-3" />
                  <h3 className="text-success mb-2">
                    {formatCurrency(revenueData.total_revenue)}
                  </h3>
                  <p className="text-muted mb-0">Total Platform Revenue</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Sessions Card */}
            <Col md={6} lg={3} className="mb-4">
              <Card className="h-100 border-primary">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faUsers} size="2x" className="text-primary mb-3" />
                  <h3 className="text-primary mb-2">
                    {revenueData.total_sessions}
                  </h3>
                  <p className="text-muted mb-0">Completed Sessions</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Average Revenue Card */}
            <Col md={6} lg={3} className="mb-4">
              <Card className="h-100 border-info">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faChartLine} size="2x" className="text-info mb-3" />
                  <h3 className="text-info mb-2">
                    {formatCurrency(revenueData.statistics?.avg_revenue_per_session)}
                  </h3>
                  <p className="text-muted mb-0">Avg Revenue per Session</p>
                </Card.Body>
              </Card>
            </Col>

            {/* Revenue Source Card */}
            <Col md={6} lg={3} className="mb-4">
              <Card className="h-100 border-warning">
                <Card.Body className="text-center">
                  <FontAwesomeIcon icon={faCoins} size="2x" className="text-warning mb-3" />
                  <h3 className="text-warning mb-2">
                    Therapy Sessions
                  </h3>
                  <p className="text-muted mb-0">Primary Revenue Source</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Monthly Breakdown */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                Monthly Revenue Breakdown
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Sessions</th>
                      <th>Revenue</th>
                      <th>Average</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueData.monthly_breakdown?.map((month) => (
                      <tr key={month.month}>
                        <td>
                          <strong>{month.month}</strong>
                        </td>
                        <td>
                          <Badge bg="primary">{month.sessions_count || 0}</Badge>
                        </td>
                        <td>
                          <span className="text-success fw-bold">
                            {formatCurrency(month.monthly_revenue)}
                          </span>
                        </td>
                        <td>
                          {formatCurrency(month.monthly_revenue / (month.sessions_count || 1))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="history" title="Revenue History">
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faHistory} className="me-2" />
                Detailed Revenue History
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Session ID</th>
                      <th>Revenue</th>
                      <th>Session Price</th>
                      <th>Therapist Payment</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueData.revenue_history?.map((revenue) => (
                      <tr key={revenue.session_id}>
                        <td>
                          <Badge bg="secondary">#{revenue.session_id}</Badge>
                        </td>
                        <td>
                          <span className="text-success fw-bold">
                            {formatCurrency(revenue.amount)}
                          </span>
                        </td>
                        <td>
                          {formatCurrency(revenue.session_details?.session_price)}
                        </td>
                        <td>
                          {formatCurrency(revenue.session_details?.therapist_payment)}
                        </td>
                        <td>
                          {formatDate(revenue.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default RevenueDashboard; 